import React, { useState } from "react";
import styled from "styled-components";
import { Icon, IconContainer } from "../visuals/styledComponents";
import { UnStyledButton } from "./styledComponents";
import { greyText, lightGreys } from "../../styles";
import { ColumnDefinition, exportToCSV } from "../../services/FileExport";
import exportIcon from "../../assets/svg/export_icon.svg";
import { CompassParagraph } from "../common/styledComponents";

const DEFAULT_MARGIN = "4px";

const StyledExportButton = styled(UnStyledButton)<{
  bottom: string;
  right: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  background: transparent;
  border: none;
  cursor: pointer;
  bottom: ${({ bottom }): string => bottom || "12px"};
  right: ${({ right }): string => right || "12px"};
`;

const StyledIconContainer = styled(IconContainer)<{ isHovered: boolean }>`
  border-radius: 6px;
  transition: background-color 0.2s ease, transform 0.2s ease;

  background-color: ${({ isHovered }): string =>
    isHovered ? lightGreys.KPIBackground : "transparent"};
  transform: ${({ isHovered }): string =>
    isHovered ? "scale(1.08)" : "scale(1)"};
`;

export const ExportButton = <
  T extends Record<string, string | number | string[]>
>({
  data,
  columns,
  filename = "export.csv",
  bottom = DEFAULT_MARGIN,
  right = DEFAULT_MARGIN,
}: ExportButtonProps<T>): JSX.Element => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const handleExport = (): void => {
    exportToCSV({ data, filename, columns });
  };

  return (
    <StyledExportButton
      bottom={bottom}
      right={right}
      onClick={handleExport}
      onMouseEnter={(): void => setIsHovered(true)}
      onMouseLeave={(): void => setIsHovered(false)}
    >
      <CompassParagraph
        fontSize={11}
        color={greyText}
        cursor="pointer"
        mb={-1}
        mr={-1}
      >
        Export to CSV
      </CompassParagraph>
      <StyledIconContainer size={28} isHovered={isHovered}>
        <Icon src={exportIcon} />
      </StyledIconContainer>
    </StyledExportButton>
  );
};

type ExportButtonProps<T> = {
  data: T[];
  columns?: ColumnDefinition<T>[];
  filename?: string;
  bottom?: string;
  right?: string;
};
